/**
 * Matching admin API failures on their machine-readable `error_code`.
 *
 * Admin routes answer expected refusals with a `ClientErrorResponse` carrying a
 * code — `phi_access_denied`, `unknown_activity_tag` — rather than a bare
 * status, so the UI can say what is actually wrong instead of "something went
 * wrong". Every such check has the same two problems, which is why they share
 * this module:
 *
 * 1. The code sits at one of two depths. `auth0BaseQuery` re-wraps every
 *    failure as `{ status, data: { status, message, data: <original body> } }`,
 *    so in the running app the backend's body is nested one level in; tests and
 *    mocks that stub the query hook hand back the raw body. Both are real
 *    shapes this has to recognise — checking only one is how a specific message
 *    silently degrades into a generic error.
 * 2. The status has to match too. A code alone is not enough: an unrelated
 *    transport failure that happens to echo a body must not be read as the
 *    backend's considered refusal.
 */

/** The shape the backend's ClientErrorResponse serialises to. */
export interface ApiErrorBody {
  error_code?: string;
  message?: string;
  data?: unknown;
}

interface MaybeQueryError {
  status?: number | string;
  data?: unknown;
}

const asBody = (value: unknown): ApiErrorBody | undefined =>
  value && typeof value === 'object' ? (value as ApiErrorBody) : undefined;

/** The error body, and the body it wraps — the code may be in either. */
export const candidateErrorBodies = (error: unknown): ApiErrorBody[] => {
  const outer = asBody(asBody(error)?.data);
  if (!outer) return [];
  const inner = asBody(outer.data);
  return inner ? [outer, inner] : [outer];
};

export interface ApiErrorMatch {
  status: number;
  errorCode: string;
}

/**
 * True when an RTK Query error is this specific, coded backend refusal — as
 * opposed to any other failure with the same status, or a transport error.
 */
export const matchesApiError = (error: unknown, { status, errorCode }: ApiErrorMatch): boolean => {
  if (!error || typeof error !== 'object') return false;
  if ((error as MaybeQueryError).status !== status) return false;
  return candidateErrorBodies(error).some(body => body.error_code === errorCode);
};

/** The refusal's own explanation, when it sent one worth showing. */
export const apiErrorMessage = (error: unknown, match: ApiErrorMatch): string | undefined => {
  if (!matchesApiError(error, match)) return undefined;
  // Prefer the body that actually carries the code — the wrapper's `message`
  // is a generic fallback the base query synthesises.
  const withCode = candidateErrorBodies(error).find(body => body.error_code === match.errorCode);
  return withCode?.message || undefined;
};
